import { create } from 'zustand'
import type { AdminAccess } from '../api/types'

export const TYPING_TTL_MS = 5000

interface EphemeralState {
  /** User ids currently online. */
  online: Record<string, true>
  /** channelId → userId → expiry epoch ms */
  typing: Record<string, Record<string, number>>
  /** channelId → userId → last read seq (other users, from ReadUpdated) */
  reads: Record<string, Record<string, number>>
  /** channelId → the reason this administrator gave for reading a conversation they are not in. */
  adminAccess: Record<string, AdminAccess>
  setOnlineUsers: (ids: string[]) => void
  setPresence: (userId: string, online: boolean) => void
  setTyping: (channelId: string, userId: string, now?: number) => void
  clearTyping: (channelId: string, userId: string) => void
  pruneTyping: (now?: number) => void
  setRead: (channelId: string, userId: string, seq: number) => void
  setAdminAccess: (channelId: string, access: AdminAccess) => void
  reset: () => void
}

export const useEphemeral = create<EphemeralState>((set, get) => ({
  online: {},
  typing: {},
  reads: {},
  adminAccess: {},
  setAdminAccess: (channelId, access) => set((state) => ({ adminAccess: { ...state.adminAccess, [channelId]: access } })),
  setOnlineUsers: (ids) => set({ online: Object.fromEntries(ids.map((id) => [id, true as const])) }),
  setPresence: (userId, online) => {
    const next = { ...get().online }
    if (online) next[userId] = true
    else delete next[userId]
    set({ online: next })
  },
  setTyping: (channelId, userId, now = Date.now()) => {
    const typing = get().typing
    set({ typing: { ...typing, [channelId]: { ...typing[channelId], [userId]: now + TYPING_TTL_MS } } })
  },
  clearTyping: (channelId, userId) => {
    const current = get().typing[channelId]
    if (!current || !(userId in current)) return
    const next = { ...current }
    delete next[userId]
    set({ typing: { ...get().typing, [channelId]: next } })
  },
  pruneTyping: (now = Date.now()) => {
    const typing = get().typing
    let changed = false
    const next: EphemeralState['typing'] = {}
    for (const [channelId, users] of Object.entries(typing)) {
      const kept = Object.fromEntries(Object.entries(users).filter(([, expires]) => expires > now))
      if (Object.keys(kept).length !== Object.keys(users).length) changed = true
      next[channelId] = kept
    }
    if (changed) set({ typing: next })
  },
  setRead: (channelId, userId, seq) => {
    const reads = get().reads
    const previous = reads[channelId]?.[userId] ?? 0
    if (seq <= previous) return
    set({ reads: { ...reads, [channelId]: { ...reads[channelId], [userId]: seq } } })
  },
  reset: () => set({ online: {}, typing: {}, reads: {}, adminAccess: {} }),
}))
